/**
 * Fuzzy send label for one scheduled family delivery.
 * Tomorrow: "sending at 7:00am Pacific".
 * One minute or more today: "sending in 5 minutes" (floored).
 * Under a minute: "sending in 29 seconds", ticking each second.
 * The number sits in a 2-digit slot so 9 and 10 do not shift the line.
 */
import { useEffect, useState } from "react"

import { countdownTickDelayMs, sendCountdown } from "#utils/familyDelivery"

const labelStyle = {
  display: "inline-flex",
  alignItems: "baseline",
  minWidth: "22ch",
  whiteSpace: "nowrap" as const,
  fontSize: 12,
  color: "#595959",
}

const numberStyle = {
  display: "inline-block",
  width: "2ch",
  textAlign: "right" as const,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontVariantNumeric: "tabular-nums" as const,
}

const unitWord = (count: number, singular: string, plural: string): string =>
  count === 1 ? singular : plural

const SendCountdownLabel = ({ notifyAt }: { notifyAt: string }) => {
  const [now, setNow] = useState(() => new Date())
  const countdown = sendCountdown(notifyAt, now)

  useEffect(() => {
    const tick = () => setNow(new Date())
    const at = new Date(notifyAt).getTime()
    if (Number.isNaN(at)) return
    const remaining = at - Date.now()
    if (sendCountdown(notifyAt, new Date()).kind === "seconds") {
      const id = window.setInterval(tick, 1000)
      return () => window.clearInterval(id)
    }
    if (remaining <= 0) return
    const id = window.setTimeout(tick, countdownTickDelayMs(remaining))
    return () => window.clearTimeout(id)
  }, [notifyAt, now])

  if (countdown.kind === "morning") {
    return (
      <span data-testid="sr-send-countdown" style={labelStyle}>
        sending at 7:00am Pacific
      </span>
    )
  }

  const unit =
    countdown.kind === "minutes"
      ? unitWord(countdown.count, "minute", "minutes")
      : unitWord(countdown.count, "second", "seconds")

  return (
    <span data-testid="sr-send-countdown" style={labelStyle}>
      sending in <span style={numberStyle}>{countdown.count}</span> {unit}
    </span>
  )
}

export default SendCountdownLabel
